import type { MdtPermission, MdtUser } from "@hydeck/mdt-contracts";
import { createSignal } from "solid-js";
import { apiRequest, authRequest } from "./api";

const storageKey = "user";

const steamOpenIdKeys = [
  "openid.ns",
  "openid.mode",
  "openid.op_endpoint",
  "openid.claimed_id",
  "openid.identity",
  "openid.return_to",
  "openid.response_nonce",
  "openid.assoc_handle",
  "openid.signed",
  "openid.sig",
] as const;

export type SteamCharacter = {
  id: string | number;
  name: string;
  profilePicture?: string;
  callsign?: string;
  job?: string;
};

export type SteamAuthReturn = {
  characters: SteamCharacter[];
  steamQueryParams: Record<string, string>;
};

function storedUser(): MdtUser | null {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as MdtUser) : null;
  } catch {
    localStorage.removeItem(storageKey);
    return null;
  }
}

export const [currentUser, setCurrentUser] = createSignal<MdtUser | null>(
  storedUser(),
);
export const [sessionReady, setSessionReady] = createSignal(false);

function persistUser(user: MdtUser | null) {
  setCurrentUser(user);
  if (user) {
    localStorage.setItem(storageKey, JSON.stringify(user));
  } else {
    localStorage.removeItem(storageKey);
  }
}

function isSteamCharacter(value: unknown): value is SteamCharacter {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const character = value as Partial<SteamCharacter>;
  return (
    (typeof character.id === "string" || typeof character.id === "number") &&
    typeof character.name === "string" &&
    character.name.trim().length > 0
  );
}

export function readSteamAuthReturn(location: Location): SteamAuthReturn | null {
  if (!location.pathname.replace(/\/+$/, "").endsWith("/external-auth/return")) {
    return null;
  }

  const search = new URLSearchParams(location.search);
  let characters: unknown;
  try {
    characters = JSON.parse(search.get("characters") ?? "[]");
  } catch {
    return null;
  }

  if (!Array.isArray(characters)) {
    return null;
  }

  const validCharacters = characters.filter(isSteamCharacter);
  if (validCharacters.length === 0) {
    return null;
  }

  const steamQueryParams: Record<string, string> = {};
  for (const key of steamOpenIdKeys) {
    const value = search.get(key);
    if (!value) {
      return null;
    }
    steamQueryParams[key] = value;
  }

  return {
    characters: validCharacters,
    steamQueryParams,
  };
}

export async function authenticateWithToken(accessToken: string) {
  const user = await apiRequest<MdtUser>("/auth/get-user-from-token", {
    method: "POST",
    body: { accessToken },
  });
  persistUser(user);
  return user;
}

export async function loginAsDeveloper() {
  const user = await apiRequest<MdtUser>("/auth/dev", { method: "POST" });
  persistUser(user);
  return user;
}

export async function verifySteamCharacterSelection(
  authReturn: SteamAuthReturn,
  characterId: string | number,
) {
  const user = await authRequest<MdtUser>("external-auth/verify", {
    method: "POST",
    body: {
      steamQueryParams: authReturn.steamQueryParams,
      characterId,
    },
  });
  persistUser(user);
  window.history.replaceState({}, document.title, "/");
  return user;
}

export async function bootstrapSession() {
  const user = currentUser();
  const search = new URLSearchParams(window.location.search);
  const queryToken = search.get("accessToken");

  try {
    if (
      window.location.pathname.replace(/\/+$/, "").endsWith("/reset-local") ||
      search.has("reset-local")
    ) {
      persistUser(null);
      window.history.replaceState({}, document.title, "/");
      return;
    }

    if (queryToken) {
      await authenticateWithToken(queryToken);
      window.history.replaceState({}, document.title, "/");
      return;
    }
    if (user?.accessToken) {
      await authenticateWithToken(user.accessToken);
    }
  } catch {
    persistUser(null);
  } finally {
    setSessionReady(true);
  }
}

export function logout() {
  persistUser(null);
}

export function hasPermission(
  permission?: MdtPermission,
  scope?: string,
): boolean {
  if (!permission) {
    return true;
  }
  const value = currentUser()?.permissions[permission];
  if (value === true) {
    return true;
  }
  return Boolean(scope && Array.isArray(value) && value.includes(scope));
}

export function hasCertification(certification?: string) {
  if (!certification) {
    return true;
  }
  return Boolean(
    currentUser()?.certs.some(({ name }) => name === certification),
  );
}
